import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useConfirm } from './ConfirmProvider';

const money = (value) => {
  const num = Number(value);
  if (Number.isFinite(num)) return `P${num.toFixed(2)}`;
  return 'P0.00';
};

const toNumber = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const normalizeInvoiceItems = (invoice) => {
  const rawItems = Array.isArray(invoice?.invoice_items) ? invoice.invoice_items : [];
  return rawItems.map((it) => {
    const quantity = toNumber(it.quantity, 0);
    const unitPrice = toNumber(it.unit_price, 0);
    return {
      id: it.id,
      description: it.description || '',
      quantity,
      unit_price: unitPrice,
      original_quantity: it.original_quantity === null || it.original_quantity === undefined ? null : toNumber(it.original_quantity, null),
      write_down_reason: it.write_down_reason || '',

      // local-only fields (not persisted)
      _base_quantity: quantity,
      _base_unit_price: unitPrice,
    };
  });
};

const computeTotals = (items) => {
  const originalTotal = items.reduce((sum, it) => {
    const baseQty = it.original_quantity ?? it._base_quantity ?? it.quantity;
    const baseRate = it._base_unit_price ?? it.unit_price;
    return sum + toNumber(baseQty, 0) * toNumber(baseRate, 0);
  }, 0);

  const adjustedTotal = items.reduce((sum, it) => {
    return sum + toNumber(it.quantity, 0) * toNumber(it.unit_price, 0);
  }, 0);

  const realization = originalTotal > 0 ? (adjustedTotal / originalTotal) * 100 : 100;
  return {
    originalTotal,
    adjustedTotal,
    writeDown: originalTotal - adjustedTotal,
    realization: Math.max(0, Math.min(100, realization)),
  };
};

const PreBillReview = ({ authAxios, showToast, fetchInvoices, permissions = {} }) => {
  const confirm = useConfirm();
  const [draftInvoices, setDraftInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [expandedInvoiceId, setExpandedInvoiceId] = useState(null);
  const [editingItemsByInvoiceId, setEditingItemsByInvoiceId] = useState({});

  const canConfirm = Boolean(permissions?.canCreateInvoice);

  const fetchDraftInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authAxios.get('invoices/?status=Pre_Bill&status=Draft');
      const invoices = Array.isArray(res?.data) ? res.data : (res?.data?.results || []);
      setDraftInvoices(invoices);
    } catch (err) {
      console.error(err);
      showToast?.('Failed to load pre-bill invoices', 'danger');
      setDraftInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [authAxios, showToast]);

  useEffect(() => {
    fetchDraftInvoices();
  }, [fetchDraftInvoices]);

  const toggleExpand = (invoice) => {
    setExpandedInvoiceId((prev) => {
      const next = prev === invoice.id ? null : invoice.id;
      if (next && !editingItemsByInvoiceId[invoice.id]) {
        setEditingItemsByInvoiceId((cur) => ({
          ...cur,
          [invoice.id]: normalizeInvoiceItems(invoice),
        }));
      }
      return next;
    });
  };

  const updateItemField = (invoiceId, itemIndex, field, value) => {
    setEditingItemsByInvoiceId((cur) => {
      const list = Array.isArray(cur[invoiceId]) ? [...cur[invoiceId]] : [];
      if (!list[itemIndex]) return cur;

      const current = { ...list[itemIndex] };

      if (field === 'quantity') {
        const nextQty = toNumber(value, 0);
        if (current.original_quantity == null) {
          current.original_quantity = current._base_quantity;
        }
        current.quantity = nextQty;
      } else if (field === 'unit_price') {
        current.unit_price = toNumber(value, 0);
      } else if (field === 'description') {
        current.description = value;
      } else if (field === 'write_down_reason') {
        current.write_down_reason = value;
      }

      list[itemIndex] = current;
      return { ...cur, [invoiceId]: list };
    });
  };

  const saveInvoiceChanges = async (invoiceId) => {
    try {
      setSavingId(invoiceId);
      const items = editingItemsByInvoiceId[invoiceId] || [];
      const payload = {
        items: items.map((it) => ({
          id: it.id,
          description: it.description,
          quantity: it.quantity,
          unit_price: it.unit_price,
          original_quantity: it.original_quantity,
          write_down_reason: it.write_down_reason || null,
        })),
      };
      await authAxios.patch(`invoices/${invoiceId}/`, payload);
      showToast?.('Invoice updated', 'success');
      await fetchDraftInvoices();
    } catch (err) {
      console.error(err);
      showToast?.(err?.response?.data?.detail || 'Failed to update invoice', 'danger');
    } finally {
      setSavingId(null);
    }
  };

  const confirmInvoice = async (invoiceId) => {
    if (!canConfirm) {
      showToast?.('Only partners may confirm invoices.', 'warning');
      return;
    }

    const ok = await confirm({
      title: 'Confirm invoice?',
      message: 'Confirm this invoice? It will move to Confirmed status.',
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      variant: 'warning',
    });
    if (!ok) return;

    try {
      setSavingId(invoiceId);
      await authAxios.post(`invoices/${invoiceId}/confirm/`);
      showToast?.('Invoice confirmed', 'success');
      await fetchDraftInvoices();
      await fetchInvoices?.();
      if (expandedInvoiceId === invoiceId) setExpandedInvoiceId(null);
    } catch (err) {
      console.error(err);
      showToast?.(err?.response?.data?.detail || 'Failed to confirm invoice', 'danger');
    } finally {
      setSavingId(null);
    }
  };

  const bulkConfirm = async () => {
    if (!canConfirm) {
      showToast?.('Only partners may confirm invoices.', 'warning');
      return;
    }

    if (draftInvoices.length === 0) {
      showToast?.('No Draft/Pre-Bill invoices to confirm', 'info');
      return;
    }

    const ok = await confirm({
      title: 'Confirm invoices?',
      message: `Confirm all ${draftInvoices.length} Draft/Pre-Bill invoices?`,
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      variant: 'warning',
    });
    if (!ok) return;

    try {
      setSavingId('__bulk__');
      let ok = 0;
      let fail = 0;
      for (const inv of draftInvoices) {
        try {
          await authAxios.post(`invoices/${inv.id}/confirm/`);
          ok += 1;
        } catch (err) {
          console.error(`Failed to confirm invoice ${inv.id}`, err);
          fail += 1;
        }
      }
      showToast?.(`Confirmed ${ok} invoice(s)${fail ? ` (${fail} failed)` : ''}`, fail ? 'warning' : 'success');
      await fetchDraftInvoices();
      await fetchInvoices?.();
    } finally {
      setSavingId(null);
    }
  };

  const expandedItems = useMemo(() => {
    if (!expandedInvoiceId) return [];
    return editingItemsByInvoiceId[expandedInvoiceId] || [];
  }, [editingItemsByInvoiceId, expandedInvoiceId]);

  const expandedTotals = useMemo(() => computeTotals(expandedItems), [expandedItems]);

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Pre-Bill Review</h2>
            <p className="text-gray-600">Review and adjust Draft/Pre-Bill invoices, then confirm</p>
          </div>
          {draftInvoices.length > 0 && (
            <button
              onClick={bulkConfirm}
              disabled={Boolean(savingId) || !canConfirm}
              className="px-6 py-3 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
              title={!canConfirm ? 'Only partners may confirm invoices.' : ''}
            >
              {savingId === '__bulk__' ? 'Confirming…' : `Confirm All (${draftInvoices.length})`}
            </button>
          )}
        </div>
      </div>

      {draftInvoices.length === 0 ? (
        <div className="bg-white p-8 rounded-lg shadow-md text-center">
          <p className="text-gray-600">No Draft/Pre-Bill invoices pending review</p>
        </div>
      ) : (
        <div className="space-y-4">
          {draftInvoices.map((invoice) => {
            const isExpanded = expandedInvoiceId === invoice.id;
            const items = editingItemsByInvoiceId[invoice.id] || [];
            const totals = isExpanded ? expandedTotals : computeTotals(items);

            return (
              <div key={invoice.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div
                  className="p-4 cursor-pointer hover:bg-gray-50 flex items-center justify-between"
                  onClick={() => toggleExpand(invoice)}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-4">
                      <h3 className="text-lg font-semibold text-gray-900">Invoice #{invoice.invoice_number}</h3>
                      <span className="px-3 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                        {invoice.status}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      Client: {invoice.client_full_name || `Client ${invoice.client}`} • Issue: {invoice.issue_date} • Total: {money(invoice.total_amount)}
                    </div>
                  </div>
                  <svg
                    className={`w-6 h-6 text-gray-400 transition-transform ${isExpanded ? 'transform rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>

                {isExpanded && (
                  <div className="border-t border-gray-200">
                    <div className="p-4 bg-gray-50 border-b">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Realization Rate</span>
                        <span
                          className={`text-lg font-bold ${totals.realization >= 90 ? 'text-green-600' : totals.realization >= 70 ? 'text-yellow-600' : 'text-red-600'}`}
                        >
                          {totals.realization.toFixed(1)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${totals.realization >= 90 ? 'bg-green-600' : totals.realization >= 70 ? 'bg-yellow-600' : 'bg-red-600'}`}
                          style={{ width: `${totals.realization}%` }}
                        ></div>
                      </div>
                      <div className="flex items-center justify-between mt-2 text-sm text-gray-600">
                        <span>Original: {money(totals.originalTotal)}</span>
                        <span>Adjusted: {money(totals.adjustedTotal)}</span>
                        <span>Write-down: {money(totals.writeDown)}</span>
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Line Total</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Write-down Reason</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {items.map((item, idx) => {
                            const lineTotal = toNumber(item.quantity, 0) * toNumber(item.unit_price, 0);
                            const wroteDownQty = item.original_quantity != null && toNumber(item.quantity, 0) < toNumber(item.original_quantity, 0);
                            return (
                              <tr key={item.id || idx}>
                                <td className="px-4 py-3">
                                  <input
                                    type="text"
                                    value={item.description}
                                    onChange={(e) => updateItemField(invoice.id, idx, 'description', e.target.value)}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                  />
                                </td>
                                <td className="px-4 py-3">
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={item.quantity}
                                    onChange={(e) => updateItemField(invoice.id, idx, 'quantity', e.target.value)}
                                    className={`w-24 px-2 py-1 border rounded text-sm ${wroteDownQty ? 'border-red-300 bg-red-50 text-red-700' : 'border-gray-300'}`}
                                  />
                                </td>
                                <td className="px-4 py-3">
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={item.unit_price}
                                    onChange={(e) => updateItemField(invoice.id, idx, 'unit_price', e.target.value)}
                                    className="w-28 px-2 py-1 border border-gray-300 rounded text-sm"
                                  />
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-700 font-medium">{money(lineTotal)}</td>
                                <td className="px-4 py-3">
                                  <input
                                    type="text"
                                    value={item.write_down_reason || ''}
                                    onChange={(e) => updateItemField(invoice.id, idx, 'write_down_reason', e.target.value)}
                                    placeholder="Reason (optional)"
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className="p-4 bg-gray-50 border-t flex items-center justify-end gap-3">
                      <button
                        onClick={() => setExpandedInvoiceId(null)}
                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium"
                      >
                        Close
                      </button>
                      <button
                        onClick={() => saveInvoiceChanges(invoice.id)}
                        disabled={Boolean(savingId)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        {savingId === invoice.id ? 'Saving…' : 'Save Changes'}
                      </button>
                      <button
                        onClick={() => confirmInvoice(invoice.id)}
                        disabled={Boolean(savingId) || !canConfirm}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                        title={!canConfirm ? 'Only partners may confirm invoices.' : ''}
                      >
                        {savingId === invoice.id ? 'Confirming…' : 'Confirm'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PreBillReview;
